import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, QrCode, Search, X, CheckCircle2 } from 'lucide-react';
import type { Tool } from '@shared/types/inventory';
import type { Technician } from '@shared/types';
import { getApiUrl } from '@/config';
import toast from 'react-hot-toast';
import jsQR from 'jsqr';
import { Html5Scanner } from '../components/Scanner/Html5Scanner';

interface WarehouseViewProps {
    technicians: Technician[];
    tools: Tool[];
    onUpdateTools: (tools: Tool[]) => void;
}

export const WarehouseView: React.FC<WarehouseViewProps> = ({
    technicians,
    tools,
    onUpdateTools
}) => {
    const [isScanning, setIsScanning] = useState(false);
    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
    const [selectedTechId, setSelectedTechId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    const activeTechnicians = useMemo(() => {
        return technicians.filter(t => t.active !== false);
    }, [technicians]);

    const filteredTechnicians = useMemo(() => {
        return activeTechnicians.filter(t =>
            t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.service || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [activeTechnicians, searchTerm]);

    const handleScan = (result: string) => {
        if (result) {
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
                navigator.vibrate(100);
            }
            findToolByQR(result);
        }
    };

    const findToolByQR = (qrValue: string) => {
        const rawTrimmed = (qrValue || '').trim();
        if (!rawTrimmed) return;

        const candidates: string[] = [rawTrimmed];
        const matchScanParam = rawTrimmed.match(/[?&](?:scan|id|code|inv)=([^&#\s]+)/i);
        if (matchScanParam && matchScanParam[1]) {
            candidates.unshift(decodeURIComponent(matchScanParam[1]));
        }

        try {
            if (rawTrimmed.includes('://') || rawTrimmed.startsWith('/')) {
                const url = new URL(rawTrimmed, 'http://dummy.base');
                const p = url.searchParams.get('scan') || url.searchParams.get('id') || url.searchParams.get('code') || url.searchParams.get('inv');
                if (p) candidates.unshift(decodeURIComponent(p));

                const segments = url.pathname.split('/').filter(Boolean);
                if (segments.length > 0) {
                    candidates.push(decodeURIComponent(segments[segments.length - 1]));
                }
            }
        } catch {
            // ignore
        }

        let tool: Tool | undefined;
        for (const c of candidates) {
            const scanVal = c.trim().toLowerCase();
            if (!scanVal) continue;
            tool = tools.find(t =>
                (t.qrIdentifier && t.qrIdentifier.toLowerCase() === scanVal) ||
                (t.id && t.id.toLowerCase() === scanVal) ||
                (t.inventoryNumber && t.inventoryNumber.toLowerCase() === scanVal) ||
                (t.serialNumber && t.serialNumber.toLowerCase() === scanVal) ||
                (t.name && t.name.toLowerCase() === scanVal)
            );
            if (tool) break;
        }

        if (tool) {
            setSelectedTool(tool);
            setIsScanning(false);
            toast.success(`Outil trouvé : ${tool.name}`);
        } else {
            toast.error("Aucun outil trouvé avec ce code QR.");
        }
    };

    const handleAssign = async () => {
        if (!selectedTool || !selectedTechId) {
            toast.error("Veuillez sélectionner un outil et un technicien.");
            return;
        }

        const tech = technicians.find(t => t.id === selectedTechId);
        if (!tech) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/tools/${selectedTool.id}/assign`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    technicianId: tech.id,
                    technicianName: tech.name
                })
            });

            if (res.ok) {
                const data = await res.json();
                const updatedTools = tools.map(t =>
                    t.id === selectedTool.id ? data.tool : t
                );
                onUpdateTools(updatedTools);
                toast.success(`L'outil "${selectedTool.name}" a été attribué à ${tech.name}.`);
                // Reset flow
                setSelectedTool(null);
                setSelectedTechId('');
            } else {
                toast.error("Erreur lors de l'attribution.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur réseau.");
        }
    };

    const handleReturn = async () => {
        if (!selectedTool) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/tools/${selectedTool.id}/return`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                const data = await res.json();
                const updatedTools = tools.map(t =>
                    t.id === selectedTool.id ? data.tool : t
                );
                onUpdateTools(updatedTools);
                toast.success(`L'outil "${selectedTool.name}" a été retourné au stock.`);
                setSelectedTool(null);
                setSelectedTechId('');
            } else {
                toast.error("Erreur lors du retour de l'outil.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur réseau.");
        }
    };

    const handleFileCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        toast.loading("Analyse de la photo...", { id: 'qr-scan' });

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;

                    const MAX_SIZE = 800;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_SIZE) {
                            height *= MAX_SIZE / width;
                            width = MAX_SIZE;
                        }
                    } else {
                        if (height > MAX_SIZE) {
                            width *= MAX_SIZE / height;
                            height = MAX_SIZE;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const code = jsQR(imageData.data, imageData.width, imageData.height);

                    if (code) {
                        toast.success("Code détecté !", { id: 'qr-scan' });
                        if (typeof navigator !== 'undefined' && navigator.vibrate) {
                            navigator.vibrate(100);
                        }
                        findToolByQR(code.data);
                    } else {
                        toast.error("Aucun QR détecté.", { id: 'qr-scan' });
                    }
                } catch (err) {
                    toast.error("Erreur d'analyse.", { id: 'qr-scan' });
                } finally {
                    e.target.value = '';
                }
            };
            img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
    };

    const isSelectedToolAssigned = selectedTool && (selectedTool.status === 'assigned' || !!selectedTool.assignedTo);
    const currentAssignee = isSelectedToolAssigned 
        ? technicians.find(t => t.id === selectedTool.assignedTo)
        : null;

    return (
        <div className="min-h-screen pb-28 bg-[var(--bg-color,#0f172a)] text-[var(--text-main,#f8fafc)] p-4 space-y-6 overflow-x-hidden transition-colors">
            <input
                id="warehouse-qr-input"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileCapture}
            />

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center text-primary border border-primary/20 shadow-md">
                    <Wrench size={24} />
                </div>
                <div>
                    <h1 className="text-xl font-black tracking-tight">Interface Magasinier</h1>
                    <p className="text-xs text-muted">Prêt et Retour d'équipement rapide • PWA</p>
                </div>
            </div>

            {/* Step 1: Scan Tool */}
            <section className="space-y-3">
                <h2 className="text-xs font-bold uppercase tracking-wider text-muted flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] text-primary border border-primary/20 font-bold">1</div>
                    Scanner ou Identifier l'outil
                </h2>

                {!selectedTool ? (
                    <button
                        onClick={() => setIsScanning(true)}
                        className="w-full py-8 bg-primary/10 hover:bg-primary/15 border-2 border-dashed border-primary/30 rounded-2xl flex flex-col items-center justify-center gap-3 active:scale-98 transition-all group shadow-sm"
                    >
                        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform text-primary shadow-inner">
                            <QrCode size={32} />
                        </div>
                        <span className="font-bold text-sm">Ouvrir le Scanner Caméra</span>
                    </button>
                ) : (
                    <div className="glass-panel p-4 rounded-2xl flex items-center gap-4 border border-emerald-500/30 bg-emerald-500/10 shadow-lg">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-500 shrink-0">
                            <Wrench size={24} />
                        </div>
                        <div className="flex-grow">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-sm sm:text-base leading-tight">{selectedTool.name}</h3>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase ${
                                    isSelectedToolAssigned 
                                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                                        : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                }`}>
                                    {isSelectedToolAssigned ? "Sorti" : "En Stock"}
                                </span>
                            </div>
                            <p className="text-xs text-muted font-mono mt-0.5">S/N: {selectedTool.serialNumber || 'N/A'}</p>
                            {isSelectedToolAssigned && currentAssignee && (
                                <p className="text-xs text-primary font-semibold mt-1">
                                    Détenu par : {currentAssignee.name}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => {
                                setSelectedTool(null);
                                setSelectedTechId('');
                            }}
                            className="p-2 rounded-xl hover:bg-white/10 text-muted hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                )}
            </section>

            {/* SCENARIO A: TOOL IS ALREADY ASSIGNED -> RETOUR RAPIDE */}
            {selectedTool && isSelectedToolAssigned && (
                <section className="glass-panel p-5 rounded-2xl space-y-4 border-amber-500/30 bg-amber-500/5">
                    <div className="flex items-center gap-2 text-amber-400 font-bold text-sm">
                        <CheckCircle2 size={18} />
                        <span>Réception de matériel au dépôt</span>
                    </div>
                    <p className="text-xs text-muted">
                        Cet outil est enregistré comme actuellement prêté. Vous pouvez valider son retour en rayon en un clic.
                    </p>
                    <button
                        onClick={handleReturn}
                        className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/30 active:scale-98 transition-all"
                    >
                        <CheckCircle2 size={20} />
                        <span>Valider le Retour en Stock</span>
                    </button>
                </section>
            )}

            {/* SCENARIO B: TOOL IS AVAILABLE -> ASSIGNATION TECHNICIEN */}
            {selectedTool && !isSelectedToolAssigned && (
                <section className="space-y-3">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] text-primary border border-primary/20 font-bold">2</div>
                        Assigner au Technicien
                    </h2>

                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" size={16} />
                        <input
                            type="text"
                            placeholder="Rechercher un technicien..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="glass-input w-full pl-10 py-2.5 text-xs sm:text-sm"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-2 max-h-[260px] overflow-y-auto pr-1 custom-scrollbar">
                        {filteredTechnicians.map(tech => (
                            <button
                                key={tech.id}
                                onClick={() => setSelectedTechId(tech.id)}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                    selectedTechId === tech.id
                                        ? 'bg-primary/20 border-primary shadow-md'
                                        : 'bg-white/5 border-glass-border hover:bg-white/10'
                                }`}
                            >
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shrink-0 text-sm shadow-sm" style={{ backgroundColor: tech.color || '#2C4E52' }}>
                                    {tech.name.charAt(0)}
                                </div>
                                <div className="flex-grow">
                                    <p className="font-bold text-xs sm:text-sm">{tech.name}</p>
                                    <p className="text-[10px] text-muted uppercase">{tech.service || 'Interne'}</p>
                                </div>
                                {selectedTechId === tech.id && (
                                    <div className="text-primary">
                                        <CheckCircle2 size={18} />
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={handleAssign}
                        disabled={!selectedTechId}
                        className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 font-extrabold text-sm transition-all shadow-lg ${
                            selectedTechId
                                ? 'bg-primary text-white shadow-primary/30 active:scale-98'
                                : 'bg-gray-800/60 text-gray-500 cursor-not-allowed border border-white/5'
                        }`}
                    >
                        <CheckCircle2 size={20} />
                        <span>Confirmer l'attribution</span>
                    </button>
                </section>
            )}

            {/* Scanner Modal */}
            <AnimatePresence>
                {isScanning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black flex flex-col"
                    >
                        <div className="p-4 flex items-center justify-between bg-black/50 backdrop-blur-sm absolute top-0 left-0 right-0 z-10 w-full text-white">
                            <h3 className="font-bold">Scanner l'outil</h3>
                            <button onClick={() => setIsScanning(false)} className="p-2 bg-white/10 rounded-full">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex-1 relative bg-black overflow-hidden">
                            <Html5Scanner onScan={handleScan} isScanning={isScanning} />
                        </div>

                        <div className="p-8 pb-12 text-center bg-black/80 backdrop-blur-sm absolute bottom-0 left-0 right-0 w-full flex flex-col gap-4">
                            <button
                                onClick={() => document.getElementById('warehouse-qr-input')?.click()}
                                className="w-full py-4 bg-white/10 text-white border border-white/20 font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all text-sm"
                            >
                                <QrCode size={18} />
                                Prendre une photo (Scan Manuel)
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
